import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router";

import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { RecordPage } from "@/components/record/RecordPage";
import type { RecordField } from "@/components/record/fields";
import type { RelatedRecordRow } from "@/components/record/RelatedRecords";
import type { DataTableColumn } from "@/components/table/columns";
import type { InlineEditCommitArgs } from "@/components/table/use-inline-edits";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { EquipmentIcon } from "@/components/ui/icons";
import { ApiError, apiFetch } from "@/lib/api";
import type {
  DocumentListItemResource,
  EquipmentReplacementResource,
  EquipmentResource,
  Page,
  ReferenceListItemResource,
} from "@/lib/api-types";
import { useIsReadOnly } from "@/lib/current-actor";
import { EQUIPMENT_STATUS_LABELS } from "@/lib/enum-labels";
import { labelFor } from "@/lib/field-labels";
import { formatDateCell, formatMoneyCell } from "@/lib/format";
import type { EditCommitOutcome, EditCommitPayload } from "@/lib/inline-edit";
import { showToast } from "@/lib/toast-queue";

import { AddEquipmentDocumentDialog } from "./AddEquipmentDocumentDialog";
import { ReplaceEquipmentDialog } from "./ReplaceEquipmentDialog";

/**
 * `/equipment/:id` (Story 12.1, spec judgement call 3) — built on
 * `RecordPage`/`FieldRow`, mirroring `RenewalRecord.tsx`'s/`PropertyRecord.
 * tsx`'s own shell usage, never a parallel page implementation.
 *
 * Unlike `RenewalRecord.tsx` (Owner-only writes), every editable field here
 * is gated only by `RecordPage`'s own default `!isReadOnly` check — `POST`/
 * `PATCH /equipment*` are `role=Role.MEMBER` (`app/api/equipment.py`'s own
 * module docstring), so a Member, not only an Owner, can edit here, the
 * same "everyday portfolio content" gate `AddDocumentDialog`'s own upload
 * action already uses.
 *
 * `category`/`prop_id`/`unit_id` render read-only (this row's own natural
 * key, immutable after creation — no `edit` config on any of the three).
 * `status` likewise renders read-only: it is never independently settable
 * anywhere (`app/repo/equipment.py::update_equipment` recomputes it itself
 * whenever `serial_number` is part of a write) — editing `serial_number`
 * below and watching `status` flip on the very next render is the real,
 * live proof of VC-12.1-03's own "an edit that adds the serial later"
 * assertion.
 *
 * Money fields (`purchase_price_cents`/`replacement_cost_estimate_cents`,
 * AD-7) render read-only here, exactly like `RenewalRecord.tsx`'s own
 * `current_rent_cents`/`proposed_rent_cents` and `LeaseRecord.tsx`'s own
 * cents fields — no established "type dollars, store cents" inline-edit
 * conversion exists anywhere in this codebase (the generic `kind: "text"`
 * inline editor pre-fills from the row's own raw field value, `InlineEdit
 * Cell.tsx`'s own `rawValue` doc comment — raw *cents*, not a dollar
 * string), so a plain text editor here would silently store whatever
 * number the Owner types as cents, a real 100x error the moment someone
 * types a dollar amount expecting the same convention `formatMoneyCell`
 * itself renders with. `AddEquipmentDialog.tsx` is the one real place
 * these fields are ever set, already correctly dollar-to-cents converting
 * at submit time — editing an existing value means retiring/re-adding via
 * Story 12.5's own future flow, not a bespoke inline conversion built here.
 */

const ORDERED_FIELD_KEYS: (keyof EquipmentResource & string)[] = [
  "status",
  "replaced_by_equipment_id",
  "property_nickname",
  "unit_label",
  "category",
  "make",
  "model_number",
  "serial_number",
  "capacity",
  "fuel_type",
  "specific_location",
  "purchase_date",
  "purchase_price_cents",
  "retailer_or_installer",
  "warranty_type",
  "warranty_length",
  "warranty_expiration_date",
  "has_extended_warranty",
  "claim_phone_number",
  "expected_useful_life_years",
  "replacement_cost_estimate_cents",
  "condition",
  "service_interval",
  "service_interval_months",
  "last_service_date",
];

//: Fields that are this row's own natural key or a derived, never-editable
//: value — no `edit` config on any of these regardless of role.
const READ_ONLY_FIELD_KEYS = new Set<string>([
  "status",
  "replaced_by_equipment_id",
  "property_nickname",
  "unit_label",
  "category",
]);

//: Story 12.5 (AC) — a `replaced`/`removed` row's own fields are frozen,
//: history, never further edited: every edit affordance this row already
//: had is withheld, for any role (VC-12.5-07), mirroring this codebase's
//: own general "a terminal/historical record renders read-only"
//: discipline.
const RETIRED_STATUSES = new Set(["replaced", "removed"]);

const YES_NO_OPTIONS = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

function formatYesNo(value: boolean | null): string {
  if (value === null) {
    return "—";
  }
  return value ? "Yes" : "No";
}

export function EquipmentRecord() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const isReadOnly = useIsReadOnly();
  const [addDocumentOpen, setAddDocumentOpen] = useState(false);
  //: Story 12.5's own "Replace"/"Remove" actions — mirrors `PropertyRecord.
  //: tsx`'s own `archiveOpen`/`isArchiving`/`archiveError` shape for its
  //: "Archive" `ConfirmDialog` exactly (module doc comment's own "mirrors
  //: this codebase's own ConfirmDialog-family precedent" reasoning).
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | undefined>(undefined);
  const contentRef = useRef<HTMLDivElement>(null);

  const queryKey = useMemo(() => ["equipment", "record", id] as const, [id]);

  const recordQuery = useQuery({
    queryKey,
    queryFn: () => apiFetch<EquipmentResource>(`/equipment/${id}`),
    enabled: id !== undefined,
  });

  // Mirrors `PropertyRecord.tsx`'s own identical `documentCategoryLabelByValue`
  // resolution — the record page shows a category's real, Owner-facing
  // label ("Water heater"), never the raw stored `ReferenceListItem.value`
  // ("water_heater") nobody typed in or sees anywhere else on this page.
  const equipmentCategoriesQuery = useQuery({
    queryKey: ["reference-list-items", "equipment_category"],
    queryFn: () => apiFetch<Page<ReferenceListItemResource>>("/reference-list-items?list_key=equipment_category"),
  });
  const equipmentCategoryLabelByValue = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of equipmentCategoriesQuery.data?.data ?? []) {
      map[item.value] = item.label;
    }
    return map;
  }, [equipmentCategoriesQuery.data]);

  const documentsQueryKey = useMemo(() => ["documents", "list", "for-equipment", id] as const, [id]);
  const documentsQuery = useQuery({
    queryKey: documentsQueryKey,
    queryFn: () => apiFetch<Page<DocumentListItemResource>>("/documents?page_size=200"),
    enabled: id !== undefined,
  });
  // `GET /documents` offers no entity_type/entity_id filter (only
  // category/property_id/filename, `app/api/documents.py`) — filtered here,
  // client-side, against the one unpaged fetch above. This mirrors the same
  // real constraint `tests/api/test_equipment.py`'s own VC-12.1-05 proof
  // already works around.
  const documentRows: RelatedRecordRow[] = (documentsQuery.data?.data ?? [])
    .filter((document) => document.entity_type === "equipment" && document.entity_id === id)
    .map((document) => ({
      id: document.id,
      original_filename: document.original_filename,
      category: document.category,
      uploaded_at: document.uploaded_at,
    }));

  useEffect(() => {
    const category = recordQuery.data
      ? (equipmentCategoryLabelByValue[recordQuery.data.category] ?? recordQuery.data.category)
      : undefined;
    document.title = category ? `${category} — PropDesk` : "Equipment — PropDesk";
  }, [recordQuery.data, equipmentCategoryLabelByValue]);

  function writeRecordToCache(updated: EquipmentResource) {
    queryClient.setQueryData<EquipmentResource>(queryKey, updated);
  }

  async function sendPatch(
    row: EquipmentResource,
    field: DataTableColumn<EquipmentResource>,
    payload: EditCommitPayload,
  ): Promise<EditCommitOutcome> {
    if (payload.kind !== "value") {
      throw new Error(`unexpected transition-kind payload for field "${field.key}"`);
    }
    let value: unknown = payload.value === "" ? null : payload.value;
    if (field.key === "has_extended_warranty" && typeof value === "string") {
      value = value === "true";
    }
    if (
      (field.key === "expected_useful_life_years" || field.key === "service_interval_months") &&
      typeof value === "string"
    ) {
      value = Number(value);
    }
    try {
      const updated = await apiFetch<EquipmentResource>(`/equipment/${row.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [field.key]: value }),
      });
      writeRecordToCache(updated);
      if (field.key === "serial_number") {
        showToast(
          updated.status === "active"
            ? "Serial number saved — this equipment is now Active."
            : "Serial number cleared.",
        );
      }
      return { ok: true };
    } catch (error) {
      if (error instanceof ApiError) {
        return { ok: false, message: error.message };
      }
      throw error;
    }
  }

  async function commitEdit(args: InlineEditCommitArgs<EquipmentResource>): Promise<EditCommitOutcome> {
    return sendPatch(args.row, args.column, args.payload);
  }

  //: Story 12.5 (AC, VC-12.5-07): a `replaced`/`removed` row's own fields
  //: are frozen, history, never further edited — every edit affordance
  //: below is withheld once this is true, for any role, on top of (never
  //: instead of) the ordinary Read-only gate.
  const retired = recordQuery.data !== undefined && RETIRED_STATUSES.has(recordQuery.data.status);

  const fields = useMemo<RecordField<EquipmentResource>[]>(() => {
    const editableUnless = (key: string) => (READ_ONLY_FIELD_KEYS.has(key) || retired ? {} : { edit: { kind: "text" as const } });
    return ORDERED_FIELD_KEYS.map((key) => {
      if (key === "status") {
        return { key, render: (r: EquipmentResource) => EQUIPMENT_STATUS_LABELS[r.status] ?? r.status };
      }
      if (key === "replaced_by_equipment_id") {
        // Story 12.5, VC-12.5-07: a real, working link to the equipment
        // that replaced this row, resolved server-side through the
        // existing AD-27 `equipment` registration (`app/api/equipment.py`'s
        // own `_resolve_replacement_link`) — never a new lookup here.
        // Blank ("—") for every row that isn't `replaced`.
        return {
          key,
          render: (r: EquipmentResource) =>
            r.replaced_by_url && r.replaced_by_label ? (
              <Link to={r.replaced_by_url} className="text-ink underline">
                {r.replaced_by_label}
              </Link>
            ) : (
              "—"
            ),
        };
      }
      if (key === "category") {
        return {
          key,
          render: (r: EquipmentResource) => equipmentCategoryLabelByValue[r.category] ?? r.category,
        };
      }
      if (key === "purchase_date" || key === "warranty_expiration_date" || key === "last_service_date") {
        return {
          key,
          render: (r: EquipmentResource) => (r[key] ? formatDateCell(r[key] as string) : "—"),
          ...editableUnless(key),
        };
      }
      if (key === "purchase_price_cents" || key === "replacement_cost_estimate_cents") {
        // Read-only here (module docstring) — no dollars-typed/cents-stored
        // inline-edit conversion exists in this codebase yet; mirrors
        // `RenewalRecord.tsx`'s own identical money-field treatment.
        return { key, render: (r: EquipmentResource) => formatMoneyCell(r[key]) };
      }
      if (key === "has_extended_warranty") {
        return {
          key,
          render: (r: EquipmentResource) => formatYesNo(r.has_extended_warranty),
          ...(retired ? {} : { edit: { kind: "select" as const, options: YES_NO_OPTIONS } }),
        };
      }
      if (READ_ONLY_FIELD_KEYS.has(key) || retired) {
        return { key };
      }
      return { key, edit: { kind: "text" as const } };
    });
  }, [equipmentCategoryLabelByValue, retired]);

  const status: "loading" | "not-found" | "error" | "ready" = recordQuery.isPending
    ? "loading"
    : recordQuery.isError
      ? recordQuery.error instanceof ApiError && recordQuery.error.status === 404
        ? "not-found"
        : "error"
      : "ready";

  const errorMessage = recordQuery.error instanceof ApiError ? recordQuery.error.message : undefined;
  const row = recordQuery.data;

  const addDocumentAction = isReadOnly
    ? undefined
    : {
        label: "Add document",
        onClick: () => setAddDocumentOpen(true),
        variant: "ghost" as const,
      };

  //: Story 12.5 (AC, spec judgement call 4) — "Replace"/"Remove" are only
  //: real, offered actions on an `active`/`details_needed` row (never one
  //: already `replaced`/`removed` — no re-replacing an already-retired
  //: row), and only for Owner/Member (mirrors every other equipment write
  //: this epic has already established, `role=Role.MEMBER`).
  const canRetire = !isReadOnly && !retired;

  async function confirmRemove() {
    if (row === undefined) {
      return;
    }
    setIsRemoving(true);
    setRemoveError(undefined);
    try {
      const updated = await apiFetch<EquipmentResource>(`/equipment/${row.id}/remove`, { method: "POST" });
      writeRecordToCache(updated);
      setRemoveOpen(false);
      showToast("Equipment removed.");
    } catch (error) {
      setRemoveError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsRemoving(false);
    }
  }

  return (
    <div ref={contentRef} tabIndex={-1} className="max-w-record-column-max">
      <ReadOnlyNotice className="mb-4" />

      <RecordPage<EquipmentResource>
        fields={fields}
        row={row}
        status={status}
        {...(errorMessage ? { errorMessage } : {})}
        identifier={(r) => {
          const categoryLabel = equipmentCategoryLabelByValue[r.category] ?? r.category;
          return r.make && r.model_number ? `${categoryLabel} — ${r.make} ${r.model_number}` : categoryLabel;
        }}
        backLink={{ label: "Equipment", href: "/equipment" }}
        icon={EquipmentIcon}
        tint="amber"
        onCommitEdit={commitEdit}
        {...(row && canRetire
          ? {
              secondaryActions: [
                { label: "Replace", ariaLabel: "Replace equipment", onClick: () => setReplaceOpen(true) },
                { label: "Remove", ariaLabel: "Remove equipment", onClick: () => setRemoveOpen(true) },
              ],
            }
          : {})}
        {...(row
          ? {
              relatedRecords: [
                {
                  title: "Documents",
                  itemNamePlural: "documents",
                  rows: documentRows,
                  columns: [
                    { key: "original_filename", label: labelFor("original_filename") },
                    { key: "uploaded_at", label: labelFor("uploaded_at"), render: (r) => formatDateCell(r.uploaded_at as string) },
                  ],
                  getHref: () => `/equipment/${id}`,
                  ...(addDocumentAction ? { action: addDocumentAction } : {}),
                  emptyState: {
                    body: "Add a photo of the data plate, a photo of the unit, or the manual.",
                    ...(addDocumentAction ? { action: addDocumentAction } : {}),
                  },
                },
              ],
            }
          : {})}
      />

      {row && !isReadOnly ? (
        <AddEquipmentDocumentDialog
          open={addDocumentOpen}
          onOpenChange={setAddDocumentOpen}
          equipmentId={row.id}
          onUploaded={() => {
            void queryClient.invalidateQueries({ queryKey: documentsQueryKey });
            showToast("Document added.");
          }}
        />
      ) : null}

      {row && canRetire ? (
        <ReplaceEquipmentDialog
          open={replaceOpen}
          onOpenChange={setReplaceOpen}
          oldEquipment={row}
          onReplaced={(result: EquipmentReplacementResource) => {
            writeRecordToCache(result.old_equipment);
            showToast("Equipment replaced.");
          }}
        />
      ) : null}

      {row && canRetire ? (
        <ConfirmDialog
          open={removeOpen}
          onOpenChange={(open) => {
            setRemoveOpen(open);
            if (!open) {
              setRemoveError(undefined);
            }
          }}
          title="Remove this equipment?"
          description="This equipment is removed, with no replacement — its history stays on file, but it no longer counts as current inventory."
          confirmLabel="Remove equipment"
          confirmingLabel="Removing…"
          isConfirming={isRemoving}
          onConfirm={() => void confirmRemove()}
          fallbackFocusRef={contentRef}
          {...(removeError ? { errorMessage: removeError } : {})}
        />
      ) : null}
    </div>
  );
}
